import React, { useCallback, useEffect, useState } from 'react';
import Spinner from './ui/Spinner';
import { adminApi } from '../config/axios';
import { useGlobal } from '../contexts/GlobalContext';
import { PLACEHOLDERS, SAMPLE, renderTemplate, validateSupport, waLink } from '../utils/supportContact';

const EMPTY = { whatsapp_e164: '', call_number: '', template_ar: '', template_en: '' };
const unwrap = (res) => res?.data?.data ?? null;

/**
 * "Support contact" -- the WhatsApp support shown on the customer Screen health page.
 * scope "global": the platform default. scope "company": an optional override; any field left empty falls back
 * to the global default ("Use default" clears the whole override).
 */
export default function SupportContactCard({ companyId = null }) {
  const { showToast } = useGlobal();
  const isCompany = companyId != null;
  const base = isCompany ? `/companies/${companyId}/support-contact` : '/support-contact';

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [defaults, setDefaults] = useState({ template_ar: '', template_en: '' });
  const [resolved, setResolved] = useState(null);
  const [errors, setErrors] = useState({});

  const apply = useCallback((d) => {
    const s = d?.stored ?? {};
    setForm({ whatsapp_e164: s.whatsapp_e164 ?? '', call_number: s.call_number ?? '', template_ar: s.template_ar ?? '', template_en: s.template_en ?? '' });
    setDefaults(s.defaults ?? { template_ar: '', template_en: '' });
    setResolved(d?.resolved ?? null);
  }, []);

  useEffect(() => {
    setLoading(true);
    adminApi.get(base).then((res) => apply(unwrap(res))).catch(() => showToast('error', 'Error', 'Could not load the support contact.')).finally(() => setLoading(false));
  // showToast comes from context and may change identity every render: it must not retrigger the load.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [base, apply]);

  const set = (k, v) => { setForm((p) => ({ ...p, [k]: v })); if (errors[k]) setErrors((p) => ({ ...p, [k]: undefined })); };

  const save = async () => {
    const e = validateSupport(form);
    setErrors(e);
    if (Object.keys(e).length) return;
    setSaving(true);
    try {
      const res = await adminApi.put(base, form);
      apply(unwrap(res));
      showToast('success', 'Saved', isCompany ? 'Company support contact override saved.' : 'Default support contact saved.');
    } catch (err) {
      const fe = err?.response?.data?.errors;
      if (fe) setErrors(Object.fromEntries(Object.entries(fe).map(([k, v]) => [k, v[0]])));
      showToast('error', 'Error', err?.response?.data?.message ?? 'Failed to save.');
    } finally { setSaving(false); }
  };

  const clearOverride = async () => {
    setSaving(true);
    try {
      const res = await adminApi.delete(base);
      apply(unwrap(res));
      setErrors({});
      showToast('success', 'Override removed', 'This company now uses the default support contact.');
    } catch { showToast('error', 'Error', 'Failed to clear the override.'); } finally { setSaving(false); }
  };

  if (loading) return <div className="card-padded flex justify-center py-10"><Spinner size="lg" /></div>;

  const effTpl = (k) => (form[k] && form[k].trim() ? form[k] : (isCompany ? (resolved?.[k] ?? defaults[k]) : defaults[k]));
  const previewAr = renderTemplate(effTpl('template_ar'), SAMPLE.ar);
  const previewEn = renderTemplate(effTpl('template_en'), SAMPLE.en);
  const effWa = form.whatsapp_e164 && form.whatsapp_e164.trim() ? form.whatsapp_e164.trim() : resolved?.whatsapp;
  const link = waLink(effWa, previewEn);
  const field = 'input-base';
  const err = (k) => errors[k] && <p className="text-danger text-xs mt-1" data-testid={`err-${k}`}>{errors[k]}</p>;

  return (
    <div className="card-padded" data-testid="support-contact-card">
      <div className="flex items-start justify-between mb-5 gap-4">
        <div>
          <h2 className="text-base font-semibold text-text-main">{isCompany ? 'Support contact (company override)' : 'Support contact'}</h2>
          <p className="text-text-muted text-sm mt-0.5">
            {isCompany
              ? 'Optional. Any field left empty uses the default support contact. Customers of this company see these values on the Screen health page.'
              : 'Default WhatsApp support shown to customers on the Screen health page. With no number set, the customer page hides the WhatsApp button.'}
          </p>
        </div>
        <div className="flex gap-2 flex-shrink-0">
          {isCompany && <button type="button" onClick={clearOverride} disabled={saving} className="btn-secondary" data-testid="use-default">Use default</button>}
          <button type="button" onClick={save} disabled={saving} className="btn-primary" data-testid="save">{saving ? <Spinner size="sm" /> : 'Save'}</button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="label">WhatsApp number</label>
          <input type="text" dir="ltr" value={form.whatsapp_e164} onChange={(e) => set('whatsapp_e164', e.target.value)}
            placeholder={isCompany && resolved?.whatsapp ? `Default: ${resolved.whatsapp}` : '+966500000000'}
            className={`${field} ${errors.whatsapp_e164 ? 'border-danger/60' : ''}`} data-testid="f-whatsapp" />
          <p className="text-text-muted text-xs mt-1">International format (E.164): + then country code and number, no spaces.</p>
          {err('whatsapp_e164')}
        </div>
        <div>
          <label className="label">Call number (optional)</label>
          <input type="text" dir="ltr" value={form.call_number} onChange={(e) => set('call_number', e.target.value)}
            placeholder={isCompany && resolved?.call ? `Default: ${resolved.call}` : '+966 11 000 0000'}
            className={`${field} ${errors.call_number ? 'border-danger/60' : ''}`} data-testid="f-call" />
          <p className="text-text-muted text-xs mt-1">Shown to the customer as plain text, as a fallback.</p>
          {err('call_number')}
        </div>
        <div>
          <label className="label">Message template — Arabic</label>
          <textarea dir="auto" rows={5} value={form.template_ar} onChange={(e) => set('template_ar', e.target.value)}
            placeholder={defaults.template_ar} className={`${field} ${errors.template_ar ? 'border-danger/60' : ''}`} data-testid="f-tpl-ar" />
          {err('template_ar')}
        </div>
        <div>
          <label className="label">Message template — English</label>
          <textarea dir="auto" rows={5} value={form.template_en} onChange={(e) => set('template_en', e.target.value)}
            placeholder={defaults.template_en} className={`${field} ${errors.template_en ? 'border-danger/60' : ''}`} data-testid="f-tpl-en" />
          {err('template_en')}
        </div>
      </div>

      <p className="text-text-muted text-xs mt-3">
        Placeholders: {PLACEHOLDERS.map((p) => <code key={p} className="mr-2">{`{${p}}`}</code>)}
        <span className="block mt-1">Leave a template empty to use the {isCompany ? 'default' : 'built-in'} wording.</span>
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4" data-testid="preview">
        <div>
          <div className="label">Preview — Arabic (sample values)</div>
          <pre dir="auto" className="whitespace-pre-wrap rounded-lg bg-surface-2 border border-white/[0.06] p-3 text-sm text-text-main" data-testid="preview-ar">{previewAr}</pre>
        </div>
        <div>
          <div className="label">Preview — English (sample values)</div>
          <pre dir="auto" className="whitespace-pre-wrap rounded-lg bg-surface-2 border border-white/[0.06] p-3 text-sm text-text-main" data-testid="preview-en">{previewEn}</pre>
        </div>
      </div>
      <p className="text-text-muted text-xs mt-2" data-testid="wa-state">
        {link ? <>WhatsApp button: <span dir="ltr">{effWa}</span></> : 'WhatsApp button: hidden (no number configured).'}
      </p>
    </div>
  );
}
